import { DNA } from '../visual-language'

const BUTTON_KINDS = {
  primary: 'primary',
  secondary: 'secondary',
  tertiary: 'tertiary',
}

const BUTTON_SIZES = {
  small: 'small',
  medium: 'medium',
  large: 'large',
}

export const buttonStyles = ({
  // @ts-ignore
  options: {
    size = 'medium',
    kind = 'primary',
    onBackground = false,
    // onBackground = false,
    isFocusVisible = true,
  } = {},
  // @ts-ignore
  overrides = {},
}) => ({
  appearance: 'none',
  cursor: 'pointer',
  fontFamily: DNA.tokens.alias.font.family.default,
  fontSize: DNA.tokens.dimension.font[75],
  fontWeight: DNA.tokens.font.weight.regular,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none',
  position: 'relative',
  boxSizing: 'border-box',

  // The focus ring
  '::after': {
    borderRadius: DNA.tokens.alias.dimension.borderRadius.medium,
    content: '""',
    display: 'block',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
    margin: isFocusVisible
      ? `calc(${DNA.tokens.dimension.size[75]} * -1)`
      : DNA.tokens.dimension.size[75],
    boxShadow: isFocusVisible
      ? `0 0 0 ${DNA.tokens.dimension.size[50]} ${
          onBackground ? DNA.tokens.color.white : DNA.tokens.alias.color.primary
        }`
      : 'none',

    transition:
      'opacity var(--dna-global-animation-duration-100, 130ms) ease-out, ' +
      'margin var(--dna-global-animation-duration-100, 130ms) ease-out',
  },
  transition:
    'border-color 0.2s ease-in-out, ' +
    'background-color 0.2s ease-in-out, ' +
    'color 0.2s ease-in-out',
  selectors: {
    '& > :not(template) ~ :not(template)': {
      marginRight: `calc(${DNA.tokens.dimension.size[100]} * 0)`,
      marginLeft: `calc(${DNA.tokens.dimension.size[100]} * (1 - 0))`,
    },
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  border: '1px solid',
  ...{
    [BUTTON_SIZES.small]: {
      paddingLeft: DNA.tokens.dimension.size[200],
      paddingRight: DNA.tokens.dimension.size[200],
      paddingTop: DNA.tokens.dimension.size[50],
      paddingBottom: DNA.tokens.dimension.size[50],
      height: DNA.tokens.dimension.size[700],
    },
    [BUTTON_SIZES.medium]: {
      paddingLeft: DNA.tokens.dimension.size[300],
      paddingRight: DNA.tokens.dimension.size[300],
      paddingTop: DNA.tokens.dimension.size[100],
      paddingBottom: DNA.tokens.dimension.size[100],
      height: DNA.tokens.dimension.size[1000],
    },
    [BUTTON_SIZES.large]: {
      paddingLeft: DNA.tokens.dimension.size[400],
      paddingRight: DNA.tokens.dimension.size[400],
      paddingTop: DNA.tokens.dimension.size[200],
      paddingBottom: DNA.tokens.dimension.size[200],
      height: DNA.tokens.dimension.size[1200],
    },
  }[size],
  ...{
    [BUTTON_KINDS.primary]: {
      background: onBackground
        ? DNA.tokens.color.white
        : DNA.tokens.alias.color.primary,
      borderColor: onBackground
        ? DNA.tokens.color.white
        : DNA.tokens.alias.color.primary,
      borderRadius: DNA.tokens.alias.dimension.borderRadius.medium,
      color: onBackground
        ? DNA.tokens.alias.color.primary
        : DNA.tokens.alias.color.primaryForeground,
      '&:hover:not(:disabled)': {
        color: onBackground
          ? DNA.tokens.alias.color.primary
          : DNA.tokens.alias.color.secondaryForeground,
        filter: onBackground ? 'brightness(90%)' : 'brightness(115%)',
      },
    },
    [BUTTON_KINDS.secondary]: {
      background: onBackground
        ? DNA.tokens.color.transparent
        : DNA.tokens.color.white,
      borderColor: onBackground
        ? DNA.tokens.color.white
        : DNA.tokens.alias.color.primary,
      borderRadius: DNA.tokens.alias.dimension.borderRadius.medium,
      color: onBackground
        ? DNA.tokens.color.white
        : DNA.tokens.alias.color.primary,
      '&:hover:not(:disabled)': {
        background: onBackground
          ? DNA.tokens.color.white
          : DNA.tokens.alias.color.primary,
        color: onBackground
          ? DNA.tokens.alias.color.primary
          : DNA.tokens.alias.color.primaryForeground,
        filter: onBackground ? 'brightness(100%)' : 'brightness(115%)',
      },
    },
    [BUTTON_KINDS.tertiary]: {
      background: DNA.tokens.color.transparent,
      borderColor: DNA.tokens.color.transparent,
      borderRadius: DNA.tokens.alias.dimension.borderRadius.medium,
      color: onBackground
        ? DNA.tokens.color.white
        : DNA.tokens.color.foreground,
      '&:hover:not(:disabled)': {
        background: onBackground
          ? 'hsla(0,0%,100%,.1)'
          : DNA.tokens.color.grey[200],
        borderColor: onBackground ? 'none' : DNA.tokens.color.grey[200],
      },
    },
  }[kind],
})

export interface ButtonStylesProps {
  /** Changes size. */
  size?: keyof typeof BUTTON_SIZES
  /** Changes appearance according to importance. */
  kind?: keyof typeof BUTTON_KINDS
  /** Uses lighter colors where possible, useful for dark backgrounds.
   * @default false
   */
  onBackground?: Boolean
  /** Controls whether focus state is visible or hidden. */
  isFocusVisible?: Boolean
}
